import React, { useState, useEffect } from 'react'
import { CustomPicker } from 'react-color'
import { Hue, Alpha, Saturation } from 'react-color/lib/components/common'
import tinycolor from 'tinycolor2'
import PickerSet from './PickerSet'
import PickerTextInput from './PickerTextInput'
import PickerEyeDrop from './PickerEyeDrop'
import PickerSwatch from './PickerSwatch'
import PickerPointer from './PickerPointer'
import { hexToObject } from '../../../utils/format'
import './CustomPickerComponent.scss'

const CustomColorPicker = ({ color, onChange, presetColors }) => {
  const { rgb, hsl, hsv, hex } = color
  const [hexInput, setHexInput] = useState(hex)
  const [alphaInput, setAlphaInput] = useState(`${Math.round(rgb.a * 100)}%`)

  useEffect(() => setHexInput(hex), [hex])
  useEffect(() => setAlphaInput(`${Math.round(rgb.a * 100)}%`), [rgb.a])

  const handleHexInputChange = (e) => setHexInput(e.target.value)

  const handleHexInputBlur = () => {
    if (tinycolor(hexInput).isValid()) {
      onChange(hexToObject(hexInput))
    } else {
      setHexInput(hex)
    }
  }

  const handleAlphaBlur = () => {
    const numericValue = parseFloat(alphaInput)
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 100) {
      const alpha = numericValue / 100
      const updatedRgb = { ...rgb, a: alpha }
      const tc = tinycolor(updatedRgb)
      onChange({
        hex: tc.toHex8String(),
        rgb: updatedRgb,
        hsl: tc.toHsl(),
        hsv: tc.toHsv(),
      })
      setAlphaInput(`${Math.round(numericValue)}%`)
    } else {
      setAlphaInput(`${Math.round(rgb.a * 100)}%`)
    }
  }

  return (
    <div className='viso-color-picker-component'>
      <div className='viso-color-picker-component__top'>
        <div className='viso-color-picker-component__saturation'>
          <Saturation
            hsl={hsl}
            hsv={hsv}
            onChange={onChange}
            pointer={() => <PickerPointer color={hex} />}
          />
        </div>

        <div className='viso-color-picker-component__section'>
          <PickerEyeDrop onChange={onChange} alpha={rgb.a} />
          <div className='viso-color-picker-component__sliders'>
            <div className='viso-color-picker-component__slider viso-color-picker-component__slider--hue'>
              <Hue
                hsl={hsl}
                onChange={onChange}
                pointer={() => <PickerPointer isSlider color={`hsl(${hsl.h} 100% 50%)`} />}
              />
            </div>
            <div className='viso-color-picker-component__slider viso-color-picker-component__slider--alpha'>
              <Alpha
                onChange={onChange}
                rgb={rgb}
                hsl={hsl}
                pointer={() => <PickerPointer isSlider />}
              />
            </div>
          </div>
        </div>
      </div>

      <PickerSet label='Custom Color'>
        <PickerSwatch
          color={`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${rgb.a})`}
          alpha={rgb.a}
          onClick={onChange}
        />
        <PickerTextInput
          id='hex'
          value={hexInput}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
        />
        <PickerTextInput
          id='alpha'
          value={alphaInput}
          onFocus={() => setAlphaInput((prev) => prev.replace('%', '').trim())}
          onChange={(e) => setAlphaInput(e.target.value)}
          onBlur={handleAlphaBlur}
        />
      </PickerSet>

      <PickerSet label='Theme Colors'>
        <div className='viso-color-picker-component__preset'>
          {presetColors.map((preset, index) => (
            <PickerSwatch
              key={index}
              color={preset}
              alpha={rgb.a}
              onClick={onChange}
            />
          ))}
        </div>
      </PickerSet>
    </div>
  )
}

export const CustomPickerComponent = CustomPicker(CustomColorPicker)
